import { type Ref } from 'react'
import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { LuShieldCheck, LuIdCard, LuCheck as Check } from 'react-icons/lu'
import {
  useFormColors,
  useFormContext,
  FormSection,
  TrackedInput,
  TrackedSelect,
  TrackedDatePicker,
  withFormProvider,
  type WithFormProvider,
} from '@minetrace/ui/forms'
import {
  useStep2RolesForm,
  type Step2RolesFormValues,
} from './useStep2RolesForm'
import { DriverFieldLengths, GeologistFieldLengths } from '../../../lib/fieldLengths'

const TRANSPORTISTA_ROLE_ID = 'd4e5f6a7-b8c9-0123-defa-234567890123'
const GEOLOGO_ROLE_ID = 'f6a7b8c9-d0e1-2345-fabc-456789012345'

const LICENSE_CATEGORIES = ['A-I', 'A-IIa', 'A-IIb', 'A-IIIa', 'A-IIIb', 'A-IIIc'].map((c) => ({ value: c, label: c }))

interface RoleOption {
  id: string
  code: string
  name: string
  description?: string | null
}

interface Step2RolesProps {
  ref?: Ref<unknown>
  roles: RoleOption[]
}

type InnerProps = Step2RolesProps & WithFormProvider<Step2RolesFormValues>

const ROLE_COLORS: Record<string, string> = {
  superadmin: '#c8442f',
  admin: '#d49a2a',
  operador: '#3d6fa8',
  transportista: '#8B5CF6',
  proveedor: '#2a8a87',
  geologo: '#06B6D4',
  digitador: '#EC4899',
  recepcion: '#14B8A6',
}

function Step2InnerComponent({ roles }: InnerProps) {
  const c = useFormColors()
  const { watch, setValue, formState: { errors } } = useFormContext<Step2RolesFormValues>()
  const selectedRoleIds = watch('roleIds') ?? []
  const hasTransportista = selectedRoleIds.includes(TRANSPORTISTA_ROLE_ID)
  const hasGeologo = selectedRoleIds.includes(GEOLOGO_ROLE_ID)

  const toggleRole = (id: string) => {
    if (selectedRoleIds.includes(id)) {
      setValue('roleIds', selectedRoleIds.filter((r) => r !== id), { shouldValidate: true })
    } else {
      setValue('roleIds', [...selectedRoleIds, id], { shouldValidate: true })
    }
  }

  const rolesError = errors.roleIds as { message?: string } | undefined

  return (
    <VStack gap={6} alignItems="stretch">
      <FormSection
        icon={LuShieldCheck}
        titleTid="personas.nuevo.sections.roles"
        descriptionTid="personas.nuevo.sections.rolesDescription"
      >
        {rolesError?.message && (
          <Box mb={4} p={3} bg={`${c.error}18`} borderRadius="xl" border="1px solid" borderColor={`${c.error}4D`}>
            <Text fontSize="sm" color={c.error}>{rolesError.message}</Text>
          </Box>
        )}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
          {roles.map((rol) => {
            const isSelected = selectedRoleIds.includes(rol.id)
            const color = ROLE_COLORS[rol.code] ?? c.accent
            return (
              <Box
                key={rol.id}
                as="button"
                type="button"
                p={4}
                borderRadius="xl"
                border="2px solid"
                borderColor={isSelected ? color : c.border}
                bg={isSelected ? `${color}10` : c.bgHover}
                cursor="pointer"
                textAlign="left"
                transition="all 0.2s"
                _hover={{ borderColor: color, bg: `${color}10` }}
                onClick={() => toggleRole(rol.id)}
                position="relative"
              >
                {isSelected && (
                  <Box position="absolute" top={3} right={3} width="24px" height="24px" borderRadius="full" bg={color} display="flex" alignItems="center" justifyContent="center">
                    <Check size={14} color="white" />
                  </Box>
                )}
                <Box display="inline-block" px={2} py={1} borderRadius="md" fontSize="10px" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em" bg={`${color}20`} color={color} mb={2}>
                  {rol.code}
                </Box>
                <Text fontSize="md" fontWeight="bold" color={c.text} mb={1}>{rol.name}</Text>
                {rol.description && <Text fontSize="xs" color={c.textMuted} lineClamp={2}>{rol.description}</Text>}
              </Box>
            )
          })}
        </Grid>
      </FormSection>

      {hasTransportista && (
        <FormSection
          icon={LuIdCard}
          titleTid="personas.nuevo.sections.driverData"
          descriptionTid="personas.nuevo.sections.driverDataDescription"
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            <TrackedInput name="licenseNumber" label="Número de licencia" placeholder="Q12345678" maxLength={DriverFieldLengths.licenseNumber} />
            <TrackedSelect
              name="licenseCategory"
              label="Categoría"
              options={LICENSE_CATEGORIES}
              placeholder="Seleccionar..."
            />
            <TrackedDatePicker name="licenseExpiryDate" label="Vencimiento" />
          </Grid>
        </FormSection>
      )}

      {hasGeologo && (
        <FormSection
          icon={LuIdCard}
          titleTid="personas.nuevo.sections.geologistData"
          descriptionTid="personas.nuevo.sections.geologistDataDescription"
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            <TrackedInput name="colegiatureCode" label="Código de colegiatura" placeholder="CIP-12345" maxLength={GeologistFieldLengths.colegiatureCode} />
            <TrackedDatePicker name="colegiatureExpiryDate" label="Vencimiento" />
          </Grid>
        </FormSection>
      )}

      {!hasTransportista && !hasGeologo && (
        <Box p={5} textAlign="center" bg={c.bgHover} borderRadius="xl">
          <Text fontSize="sm" color={c.textMuted}>
            Si el usuario es <Text as="span" fontWeight="600" color={c.text}>transportista</Text> o <Text as="span" fontWeight="600" color={c.text}>geólogo</Text>, al seleccionarlo aparecerán los campos específicos.
          </Text>
        </Box>
      )}

      <Flex gap={2} flexWrap="wrap">
        {selectedRoleIds.map((roleId) => {
          const rol = roles.find((r) => r.id === roleId)
          if (!rol) return null
          const color = ROLE_COLORS[rol.code] ?? c.accent
          return (
            <Box key={roleId} display="inline-block" px={3} py={1.5} borderRadius="lg" fontSize="xs" fontWeight="bold" bg={`${color}20`} color={color}>
              {rol.name}
            </Box>
          )
        })}
      </Flex>
    </VStack>
  )
}

const Step2Inner = withFormProvider(Step2InnerComponent, useStep2RolesForm)

const Step2Roles = ({ ref, roles, ...rest }: Step2RolesProps & { defaultValues?: Step2RolesFormValues }) => (
  <Step2Inner ref={ref} roles={roles} {...rest} />
)

export default Step2Roles
